'use client';

import { toast } from "@/components/ui/use-toast";
import { Comment, ParentType, RestaurantReview } from "@/types/confession";
import { MapsPlace } from "@/types/maps-place";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";

export function useRestaurantComments(restaurant: MapsPlace) {
  const [isLoading, setIsLoading] = useState(true);
  const [comments, setComments] = useState<Comment[]>([]);

  const fetchComments = async () => {
    try {
      const response = await fetch(`/api/comments?parentId=${restaurant.id}&parentType=${ParentType.RESTAURANT}`);
      if (!response.ok) throw new Error('Failed to fetch comments');
      
      const dbComments = await response.json();
      
      // Combine Google reviews with DB comments
      const googleReviews = restaurant.reviews?.map(review => ({
        id: nanoid(8).toLowerCase(),
        content: review.text.text,
        user: {
          id: nanoid(8).toLowerCase(),
          username: review.authorAttribution.displayName
        },
        createdAt: new Date(review.publishTime),
        likeCount: 0,
        likedBy: [],
        replies: [],
        parentId: restaurant.id,
        parentType: ParentType.RESTAURANT,
        parentCommentId: null,
        topLevelCommentId: null,
        rating: review.rating
      } as RestaurantReview)) || [];

      const formattedDbComments = dbComments.map((comment: any) => ({
        id: comment.id,
        content: comment.content,
        user: {
          id: comment.userId,
          username: comment.userName
        },
        createdAt: new Date(comment.createdAt),
        likeCount: comment.likeCount,
        likedBy: comment.likedBy,
        parentId: comment.ParentId,
        parentType: comment.ParentType,
        parentCommentId: comment.parentCommentId,
        topLevelCommentId: comment.topLevelCommentId,
        rating: comment.rating
      } as RestaurantReview)) || [];

      setComments([...formattedDbComments, ...googleReviews]);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast({
        title: "Error",
        description: "Failed to load reviews. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refetchComments = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/comments?parentId=${restaurant.id}&parentType=RESTAURANT`);
      if (!response.ok) throw new Error('Failed to fetch comments');
      const data = await response.json();
      setComments(data);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast({
        title: "Error",
        description: "Failed to refresh comments. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [restaurant.id]);

  return { comments, setComments, isLoading, refetchComments };
} 